import { useState, useRef, useCallback } from "react";
import type { PlaceResult } from "@poi-planner/shared";
import { PLACES_SEARCH_DEBOUNCE_MS } from "@poi-planner/shared";
import {
  searchPlaces,
  resolvePlaceUrl,
  isGoogleMapsUrl,
  PlacesApiError,
} from "../services/placesService";

export interface UsePlacesSearchResult {
  results: PlaceResult[];
  loading: boolean;
  error: string | null;
  search: (query: string) => void;
  clear: () => void;
}

export function usePlacesSearch(): UsePlacesSearchResult {
  const [results, setResults] = useState<PlaceResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback((query: string) => {
    // Clear any pending debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const trimmed = query.trim();

    // Clear results if query is empty
    if (!trimmed) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Check if input is a Google Maps URL - resolve immediately (no debounce)
    if (isGoogleMapsUrl(trimmed)) {
      (async () => {
        try {
          const result = await resolvePlaceUrl(trimmed);
          if (result) {
            setResults([result]);
          } else {
            setError("Could not extract location from URL");
            setResults([]);
          }
        } catch (err) {
          if (err instanceof PlacesApiError) {
            setError(err.message);
          } else {
            setError("Failed to resolve URL");
          }
          setResults([]);
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    // Debounced text search
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await searchPlaces(trimmed);
        setResults(response.results);
        setError(null);
      } catch (err) {
        if (err instanceof PlacesApiError) {
          setError(err.message);
        } else {
          setError(err instanceof Error ? err.message : "Search failed");
        }
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, PLACES_SEARCH_DEBOUNCE_MS);
  }, []);

  const clear = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setResults([]);
    setLoading(false);
    setError(null);
  }, []);

  return { results, loading, error, search, clear };
}
